import { useEffect, useRef, useState } from "react";

const TURNSTILE_API_URL =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const TURNSTILE_SCRIPT_ID = "cf-turnstile-script";

const loadTurnstileScript = () => {
  if (window.turnstile) {
    return Promise.resolve();
  }

  if (window.__turnstileLoadPromise) {
    return window.__turnstileLoadPromise;
  }

  window.__turnstileLoadPromise = new Promise((resolve, reject) => {
    const existingScript = document.getElementById(TURNSTILE_SCRIPT_ID);
    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(), { once: true });
      existingScript.addEventListener(
        "error",
        () => reject(new Error("Failed to load Turnstile script.")),
        { once: true },
      );
      return;
    }

    const script = document.createElement("script");
    script.id = TURNSTILE_SCRIPT_ID;
    script.src = TURNSTILE_API_URL;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error("Failed to load Turnstile script."));
    document.head.appendChild(script);
  });

  return window.__turnstileLoadPromise;
};

const TurnstileWidget = ({
  siteKey,
  onTokenChange,
  onError,
  resetCounter = 0,
}) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onTokenChangeRef = useRef(onTokenChange);
  const onErrorRef = useRef(onError);
  const [ready, setReady] = useState(Boolean(window.turnstile));

  useEffect(() => {
    onTokenChangeRef.current = onTokenChange;
  }, [onTokenChange]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    let isMounted = true;

    loadTurnstileScript()
      .then(() => {
        if (isMounted) {
          setReady(true);
        }
      })
      .catch(() => {
        onTokenChangeRef.current?.("");
        onErrorRef.current?.(
          "Security verification failed to load. Please refresh and try again.",
        );
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!ready || !window.turnstile || !containerRef.current || !siteKey) {
      return;
    }

    containerRef.current.innerHTML = "";
    widgetIdRef.current = window.turnstile.render(containerRef.current, {
      sitekey: siteKey,
      callback: (token) => onTokenChangeRef.current?.(token),
      "expired-callback": () => onTokenChangeRef.current?.(""),
      "error-callback": () => {
        onTokenChangeRef.current?.("");
        onErrorRef.current?.(
          "Security verification failed. Please retry the challenge.",
        );
      },
    });

    return () => {
      if (widgetIdRef.current !== null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [ready, siteKey]);

  useEffect(() => {
    if (!ready || widgetIdRef.current === null || !window.turnstile) {
      return;
    }

    window.turnstile.reset(widgetIdRef.current);
    onTokenChangeRef.current?.("");
  }, [ready, resetCounter]);

  if (!siteKey) {
    return (
      <div style={{ color: "#d32f2f", marginTop: "6px" }}>
        Security verification is not configured.
      </div>
    );
  }

  return <div ref={containerRef} />;
};

export default TurnstileWidget;
